// i18n 基礎(react-i18next)。EPIC APP#15 web 試點。
// 4 語系:繁中(zh-Hant,權威/fallback)、簡中(zh-Hans)、英文(en)、日文(ja)。
// ⚠️ 不用 i18next-browser-languagedetector:該版本與內聯 resources 併用時會使
// namespace 標記不到 loaded → t() 永遠回 key(連 production 都壞)。改手動偵測
// 初始語系 + 切換時寫 localStorage('ls.lang'),效果相同且穩定。
// resources 內聯打包(無 http backend)→ 同步就緒,t() 立即可用。
// ⚠️ en/ja(及部分 zh-Hans)為 AI 初稿,待真人校(see locales/*,EPIC APP#15)。
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './locales/en.json';
import zhHant from './locales/zh-Hant.json';
import zhHans from './locales/zh-Hans.json';
import ja from './locales/ja.json';

export const SUPPORTED_LANGUAGES = [
  { code: 'zh-Hant', label: '繁體中文' },
  { code: 'zh-Hans', label: '简体中文' },
  { code: 'en', label: 'English' },
  { code: 'ja', label: '日本語' },
] as const;

export type LangCode = (typeof SUPPORTED_LANGUAGES)[number]['code'];

export const FALLBACK_LANG: LangCode = 'zh-Hant';
export const LANG_STORAGE_KEY = 'ls.lang';

function isSupported(code: string | null | undefined): code is LangCode {
  return !!code && SUPPORTED_LANGUAGES.some((l) => l.code === code);
}

/** 初始語系:localStorage(使用者選定)→ navigator → fallback。 */
export function detectInitialLang(): LangCode {
  try {
    const saved = globalThis.localStorage?.getItem(LANG_STORAGE_KEY);
    if (isSupported(saved)) return saved;
  } catch {
    /* SSR / 隱私模式 */
  }
  const navs =
    typeof navigator !== 'undefined'
      ? navigator.languages?.length
        ? navigator.languages
        : [navigator.language]
      : [];
  for (const raw of navs) {
    const low = (raw ?? '').toLowerCase();
    if (low.startsWith('zh')) {
      return low.includes('hans') || low.includes('cn') || low.includes('sg') ? 'zh-Hans' : 'zh-Hant';
    }
    if (low.startsWith('ja')) return 'ja';
    if (low.startsWith('en')) return 'en';
  }
  return FALLBACK_LANG;
}

void i18n.use(initReactI18next).init({
  resources: {
    'zh-Hant': { translation: zhHant },
    'zh-Hans': { translation: zhHans },
    en: { translation: en },
    ja: { translation: ja },
  },
  lng: detectInitialLang(),
  fallbackLng: FALLBACK_LANG,
  supportedLngs: ['zh-Hant', 'zh-Hans', 'en', 'ja'],
  interpolation: { escapeValue: false }, // React 已防 XSS
  returnNull: false,
});

/** 同步 <html lang>(a11y / SEO;design review)。 */
function syncDocumentLang(lng: string): void {
  if (typeof document !== 'undefined') document.documentElement.lang = lng;
}
// 初始 + 之後每次切換都更新 <html lang>（languageChanged 涵蓋 setLanguage 與任何 changeLanguage）。
syncDocumentLang(i18n.language || FALLBACK_LANG);
i18n.on('languageChanged', syncDocumentLang);

/** 切換語系並持久化(LanguageSwitcher 用)。 */
export function setLanguage(code: LangCode): void {
  void i18n.changeLanguage(code);
  try {
    globalThis.localStorage?.setItem(LANG_STORAGE_KEY, code);
  } catch {
    /* SSR / 隱私模式 → 退化為本 session 記憶 */
  }
}

export default i18n;
